import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import {
  changeEventStatus,
  checkEventPublicRoute,
  deleteEvent,
  duplicateEvent,
  generateEventBrochure,
  getBrochureTemplates,
  getEvent,
  getEventAudit,
  getEventBrochures,
  getEventProgramOptions,
  getEventRegistrations,
  getPrograms,
  previewEvent,
  releaseEventAlias,
  updateEvent,
  uploadEventBrochure,
} from '@/lib/api';
import {
  ATTENDANCE_MODES,
  EVENT_EDITOR_TABS,
  EVENT_LOCALES,
  MAX_EVENT_PROGRAMS,
  ROUTE_DESTINATION_TYPES,
  ROUTE_STATUS_OPTIONS,
  attendanceModeLabel,
  canTransition,
  emptyTranslation,
  eventDeleteImpact,
  eventStatusMeta,
  eventTitle,
  formatDateTime,
  isPastEvent,
  isPublishable,
  localeLabel,
  normalizeProgramSlug,
  normalizePublicPath,
  programOptionsWithSelected,
  publicUrlFor,
  publicOriginOf,
  publishBlockers,
  routeStatusMeta,
  staffReferenceCodeErrors,
} from '@/lib/eventStudio';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { StatusPill, adminInputClass } from '@/components/admin/AdminTableStyles';
import EventBrochureManager from '@/components/admin/EventBrochureManager';
import EventAgencyLinks from '@/components/admin/EventAgencyLinks';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { ADMIN_SCOPES, hasAdminScope } from '@/lib/auth';
import {
  AlertTriangle, ArrowLeft, CalendarDays, ChevronDown, Copy, ExternalLink, Eye,
  Loader2, Plus, RefreshCw, Save, Trash2, Upload,
} from 'lucide-react';

const inputClass = adminInputClass;

/** Fields the editor owns. Ids, counters and audit stamps are server-side. */
const EDITABLE = [
  'slug', 'locales', 'translations', 'start_at', 'end_at', 'timezone',
  'registration_opens_at', 'registration_closes_at', 'attendance_mode', 'capacity',
  'waitlist_enabled', 'private_address', 'private_access_instructions',
  'private_meeting_url', 'private_meeting_id', 'private_meeting_passcode',
  'private_notes', 'contact_email', 'contact_phone', 'campaign_links',
  'featured', 'featured_priority', 'programs',
  // Identity and routing. Five separate things: the UUID is the key, the EVT id
  // is the immutable system id, the staff reference code is an editable
  // internal label, the slug is the internal website slug, and the route holds
  // the public address. Only the last two are visible to a visitor.
  'staff_reference_code', 'public_route',
];

/** Which confirm flag a 409 from the server is asking for. */
const CONFIRM_FLAG_FOR_FIELD = {
  staff_reference_code: 'confirm_staff_reference_change',
  public_url: 'confirm_public_route_change',
};

/** Trim an ISO timestamp down to what <input type="datetime-local"> accepts. */
function toLocalInput(value) {
  const text = String(value || '');
  return text ? text.slice(0, 16) : '';
}

function fromLocalInput(value) {
  const text = String(value || '').trim();
  return text ? `${text}:00` : '';
}

function Field({ label, htmlFor, hint, children }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {hint && <p className="text-xs text-[#062B1A]/45">{hint}</p>}
    </div>
  );
}

/**
 * Which program pages on the marketing site this event belongs on.
 *
 * Chips for everything the catalog knows about, plus a box for a slug it does
 * not — marketing publishes a program page before anyone edits the catalog, and
 * an event should be taggable for it that same afternoon. Free entry is
 * normalized to the stored form as it is typed so nobody saves "Global_Hospitality"
 * and then wonders why the page is empty.
 */
function ProgramPicker({ options, selected, onChange }) {
  const [draft, setDraft] = useState('');
  const chosen = Array.isArray(selected) ? selected : [];
  const all = programOptionsWithSelected(options, chosen);
  const atLimit = chosen.length >= MAX_EVENT_PROGRAMS;

  const toggle = (slug) => onChange(chosen.includes(slug)
    ? chosen.filter((item) => item !== slug)
    : [...chosen, slug].slice(0, MAX_EVENT_PROGRAMS));

  const addDraft = () => {
    const slug = normalizeProgramSlug(draft);
    setDraft('');
    if (slug && !chosen.includes(slug)) toggle(slug);
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {all.length === 0 && <p className="text-sm text-[#062B1A]/45">No programs available.</p>}
        {all.map((option) => {
          const active = chosen.includes(option.slug);
          return (
            <Button
              key={option.slug} type="button" size="sm" variant="outline"
              aria-pressed={active}
              disabled={!active && atLimit}
              title={option.source === 'portal'
                ? `${option.label} — a portal program; the website may not have a page for it yet.`
                : option.label}
              onClick={() => toggle(option.slug)}
              className={cn('h-8 rounded-full text-xs',
                active ? 'border-[#D4AF37] bg-[#062B1A] text-white' : 'border-[#062B1A]/15 text-[#062B1A]')}
            >
              {option.label}
              <span className={cn('ml-1.5 font-mono text-[10px]',
                active ? 'text-white/60' : 'text-[#062B1A]/40')}>{option.slug}</span>
            </Button>
          );
        })}
      </div>
      <div className="flex gap-2">
        <Input
          aria-label="Add a program slug"
          value={draft}
          disabled={atLimit}
          placeholder="Another program slug, e.g. global-hospitality"
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key !== 'Enter') return;
            event.preventDefault();
            addDraft();
          }}
          className={cn('h-8 flex-1', inputClass)}
        />
        <Button
          type="button" size="sm" variant="outline" onClick={addDraft}
          disabled={atLimit || !normalizeProgramSlug(draft)}
          className="h-8 border-[rgba(6,43,26,0.16)] text-xs text-[#062B1A]"
        >
          <Plus className="mr-1 size-3.5" /> Add
        </Button>
      </div>
      {draft.trim() && normalizeProgramSlug(draft) !== draft.trim() && (
        <p className="text-xs text-[#062B1A]/45">
          Will be saved as <span className="font-mono">{normalizeProgramSlug(draft) || '—'}</span>
        </p>
      )}
    </div>
  );
}

/** Repeating title+body rows: benefits, steps, agenda and FAQs share this. */
function ListEditor({ label, items, onChange, withTime = false, addLabel = 'Add item' }) {
  const rows = Array.isArray(items) ? items : [];
  const update = (index, patch) => onChange(rows.map((row, position) => (
    position === index ? { ...row, ...patch } : row
  )));
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-[#062B1A]/45">{label}</p>
        <Button
          type="button" size="sm" variant="outline"
          onClick={() => onChange([...rows, withTime
            ? { time: '', start_at: '', title: '', body: '' }
            : { title: '', body: '' }])}
          className="h-7 border-[rgba(6,43,26,0.16)] text-xs text-[#062B1A]"
        >
          <Plus className="mr-1 size-3.5" /> {addLabel}
        </Button>
      </div>
      {rows.length === 0 && <p className="text-sm text-[#062B1A]/45">Nothing added yet.</p>}
      {rows.map((row, index) => (
        <div key={index} className="space-y-2 rounded-lg border border-[rgba(6,43,26,0.10)] p-3">
          <div className="flex flex-wrap gap-2">
            {withTime && (
              <Input
                aria-label={`${label} time ${index + 1}`}
                value={row.time || ''}
                placeholder="6:00 PM"
                onChange={(event) => update(index, { time: event.target.value })}
                className={cn('h-8 w-28', inputClass)}
              />
            )}
            {/* Optional and separate from the label above: the label is what a
                visitor reads, this is what a screen reader and a search engine
                read out of the <time> element. Blank is fine. */}
            {withTime && (
              <Input
                type="datetime-local"
                aria-label={`${label} exact time ${index + 1}`}
                value={toLocalInput(row.start_at)}
                onChange={(event) => update(index, { start_at: fromLocalInput(event.target.value) })}
                className={cn('h-8 w-52', inputClass)}
              />
            )}
            <Input
              aria-label={`${label} title ${index + 1}`}
              value={row.title || ''}
              placeholder="Heading"
              onChange={(event) => update(index, { title: event.target.value })}
              className={cn('h-8 flex-1', inputClass)}
            />
            <Button
              type="button" size="icon" variant="ghost"
              aria-label={`Remove ${label} ${index + 1}`}
              onClick={() => onChange(rows.filter((_, position) => position !== index))}
              className="h-8 w-8 text-[rgb(153,27,27)]"
            >
              <Trash2 className="size-3.5" />
            </Button>
          </div>
          <Textarea
            aria-label={`${label} body ${index + 1}`}
            rows={2}
            value={row.body || ''}
            placeholder="Detail (optional)"
            onChange={(event) => update(index, { body: event.target.value })}
            className={inputClass}
          />
        </div>
      ))}
    </div>
  );
}

export default function AdminEventEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const canManageBrochures = hasAdminScope(profile, ADMIN_SCOPES.STUDENT);
  // Same scope the server gates events.edit on: whoever may edit the event may
  // generate the links that promote it.
  const canManageLinks = canManageBrochures;

  const [event, setEvent] = useState(null);
  const [form, setForm] = useState(null);
  const [tab, setTab] = useState('basics');
  const [locale, setLocale] = useState('en');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const [brochures, setBrochures] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [preview, setPreview] = useState(null);
  const [audit, setAudit] = useState([]);
  const [registrations, setRegistrations] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [programOptions, setProgramOptions] = useState([]);
  const [technicalOpen, setTechnicalOpen] = useState(false);
  const [confirmChange, setConfirmChange] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [checkingRoute, setCheckingRoute] = useState(false);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await getEvent(id);
      setEvent(data);
      setForm(Object.fromEntries(EDITABLE.map((key) => [key, data[key]])));
      setNotFound(false);
    } catch (error) {
      console.error(error);
      if (error?.response?.status === 404) setNotFound(true);
      else toast.error(error?.response?.data?.detail || 'Could not load this event');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const refreshBrochures = useCallback(async () => {
    const { data } = await getEventBrochures(id);
    setBrochures(data?.items || []);
    return data?.items || [];
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!event) return;
    refreshBrochures().catch(console.error);
    getBrochureTemplates().then(({ data }) => setTemplates(data?.templates || [])).catch(console.error);
    // The program tags live on the Basics tab, which is where the editor opens,
    // so this is fetched with the event rather than on a tab change.
    getEventProgramOptions()
      .then(({ data }) => setProgramOptions(data?.items || []))
      .catch(console.error);
  }, [event, id, refreshBrochures]);

  useEffect(() => {
    if (tab === 'publish' && event) {
      getEventAudit(id).then(({ data }) => setAudit(data?.entries || [])).catch(console.error);
    }
    if (tab === 'registration' && event) {
      getEventRegistrations(id, { limit: 5 }).then(({ data }) => setRegistrations(data)).catch(console.error);
    }
    // The related-program picker only matters on the routing tab.
    if (tab === 'social' && programs.length === 0) {
      getPrograms().then(({ data }) => setPrograms(
        Array.isArray(data) ? data : (data?.items || []),
      )).catch(console.error);
    }
  }, [tab, event, id, programs.length]);

  const setField = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const translation = useMemo(() => ({
    ...emptyTranslation(),
    ...((form?.translations || {})[locale] || {}),
  }), [form, locale]);

  const setTranslationField = (field, value) => setForm((current) => ({
    ...current,
    translations: {
      ...(current.translations || {}),
      [locale]: { ...emptyTranslation(), ...((current.translations || {})[locale] || {}), [field]: value },
    },
  }));

  const save = async (extra = {}) => {
    if (saving || !form) return;
    try {
      setSaving(true);
      const { data } = await updateEvent(id, { ...form, ...extra });
      setEvent(data);
      setForm(Object.fromEntries(EDITABLE.map((key) => [key, data[key]])));
      setConfirmChange(null);
      toast.success('Event saved');
    } catch (error) {
      console.error(error);
      const detail = error?.response?.data?.detail;
      // The server refuses to change an identifier it already has until it is
      // told to, and hands back both values so the dialog can show them.
      if (detail && typeof detail === 'object' && !Array.isArray(detail) && detail.field) {
        setConfirmChange(detail);
        return;
      }
      toast.error(
        Array.isArray(detail) ? detail.join(' ')
          : typeof detail === 'string' ? detail
            : 'Could not save this event',
      );
    } finally {
      setSaving(false);
    }
  };

  const confirmAndSave = () => {
    const flag = CONFIRM_FLAG_FOR_FIELD[confirmChange?.field];
    if (!flag) { setConfirmChange(null); return; }
    save({ [flag]: true });
  };

  // ─── Public routing ───
  // The route lives on the event record. Nothing here decides an address from
  // which event it is; it reads and writes what the event stores.
  const route = form?.public_route || {};
  // Only the scheme and host: the field below renders `origin + path`, so an
  // origin holding any path would prepend that path again on every keystroke.
  const publicOrigin = route.origin || publicOriginOf(event?.public_url)
    || 'https://metropolitancollege.ca';
  const setRoute = (patch) => setForm((current) => ({
    ...current,
    public_route: { ...(current.public_route || {}), ...patch },
  }));

  const setPublicUrl = (value) => {
    const text = String(value || '').trim();
    if (!text) { setRoute({ path: '' }); return; }
    const match = text.match(/^(https:\/\/[^/]+)(\/.*)?$/i);
    if (!match) { setRoute({ path: normalizePublicPath(text) }); return; }
    const origin = match[1].replace(/\/+$/, '');
    setRoute({
      origin: origin === 'https://metropolitancollege.ca' ? '' : origin,
      path: normalizePublicPath(match[2] || ''),
    });
  };

  const runRouteCheck = async () => {
    try {
      setCheckingRoute(true);
      const { data } = await checkEventPublicRoute(id);
      toast[data?.status === 'verified' ? 'success' : 'error'](
        data?.note || 'Route checked',
      );
      await load();
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || 'Could not check the public route');
    } finally {
      setCheckingRoute(false);
    }
  };

  const releaseAlias = async (kind, value) => {
    try {
      await releaseEventAlias(id, { kind, value });
      toast.success(`${value} released`);
      await load();
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || 'Could not release it');
    }
  };

  const moveStatus = async (status) => {
    try {
      const { data } = await changeEventStatus(id, { status });
      setEvent(data);
      toast.success(`Event ${eventStatusMeta(status).label.toLowerCase()}`);
    } catch (error) {
      console.error(error);
      const detail = error?.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail.join(' ') : detail || 'Could not change the status');
    }
  };

  // Any event can be deleted, at any status — the dialog is what makes it a
  // decision rather than a click, and the server refuses a consequential delete
  // that did not come through it.
  const removeEvent = async () => {
    if (deleting) return;
    try {
      setDeleting(true);
      const { data } = await deleteEvent(id, { confirm: true });
      const unlinked = Number(data?.registrations_unlinked) || 0;
      toast.success(unlinked
        ? `Event deleted — ${unlinked} registration${unlinked === 1 ? '' : 's'} kept and unlinked`
        : 'Event deleted');
      navigate('/admin/events');
    } catch (error) {
      console.error(error);
      const detail = error?.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail.join(' ')
        : typeof detail === 'string' ? detail : 'Could not delete this event');
      setDeleting(false);
    }
  };

  // The copy is a fresh draft with its own identity; the editor follows it so
  // the next thing staff do is edit the copy, not the original.
  const duplicate = async () => {
    if (duplicating) return;
    try {
      setDuplicating(true);
      const { data } = await duplicateEvent(id, {});
      toast.success(`${data.event_id} created from a copy`);
      navigate(`/admin/events/${data.id}`);
    } catch (error) {
      console.error(error);
      const detail = error?.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail.join(' ')
        : typeof detail === 'string' ? detail : 'Could not duplicate this event');
    } finally {
      setDuplicating(false);
    }
  };

  const loadPreview = async () => {
    try {
      const { data } = await previewEvent(id, locale);
      setPreview(data?.event || null);
    } catch (error) {
      console.error(error);
      toast.error('Could not build the preview');
    }
  };

  const onUpload = async (file) => {
    if (!file) return;
    try {
      setUploading(true);
      await uploadEventBrochure(id, file, { title: file.name, locale });
      await refreshBrochures();
      toast.success('Brochure uploaded');
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || 'Could not upload the brochure');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const generate = async (templateId) => {
    try {
      setGenerating(true);
      const { data } = await generateEventBrochure(id, { template_id: templateId, locale });
      await refreshBrochures();
      toast[data?.rendered ? 'success' : 'error'](
        data?.rendered ? `Brochure ${data.title} created` : data?.render_note || 'Brochure data saved without a PDF',
      );
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || 'Could not build the brochure');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="animate-fade-in space-y-4">
        <div className="h-8 w-64 animate-pulse rounded bg-[#F7F8FA]" />
        <div className="h-96 animate-pulse rounded-xl bg-[#F7F8FA]" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="animate-fade-in space-y-4">
        <Button variant="outline" size="sm" onClick={() => navigate('/admin/events')}
          className="border-[rgba(6,43,26,0.16)] text-[#062B1A]">
          <ArrowLeft className="mr-1 h-4 w-4" /> Back to events
        </Button>
        <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
          <CardContent className="p-10 text-center text-sm text-[#062B1A]/55">
            This event no longer exists.
          </CardContent>
        </Card>
      </div>
    );
  }

  const meta = eventStatusMeta(event?.status);
  const past = isPastEvent(event);
  const deleteImpact = eventDeleteImpact(event);
  const blockers = publishBlockers(event);
  const permissions = event?.permissions || {};
  const permissionHints = event?.permission_hints || {};
  // Visible but disabled, with the reason beside it, rather than absent.
  const canEditReference = permissions['events.edit_staff_reference'] === true;
  const canEditRoute = permissions['events.edit_public_route'] === true;
  const referenceHint = permissionHints['events.edit_staff_reference']
    || 'Only a full administrator can change the staff reference code.';
  const routeHint = permissionHints['events.edit_public_route']
    || 'Only a full administrator can change the public event URL.';
  const referenceErrors = form.staff_reference_code
    ? staffReferenceCodeErrors(form.staff_reference_code) : [];
  const routeMeta = routeStatusMeta(event?.public_route?.status);
  const previewUrl = publicUrlFor(publicOrigin, route.path) || event?.public_url || '';
  const referenceAliases = event?.staff_reference_aliases || [];
  const routeAliases = event?.public_route?.aliases || [];

  return (
    <div className="animate-fade-in space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-2">
          <Button variant="outline" size="sm" onClick={() => navigate('/admin/events')}
            className="border-[rgba(6,43,26,0.16)] text-[#062B1A]">
            <ArrowLeft className="mr-1 h-4 w-4" /> Back to events
          </Button>
          <h2 className="flex items-center gap-2 text-2xl font-semibold text-[#062B1A]"
            style={{ fontFamily: 'var(--font-display)' }}>
            <CalendarDays className="h-6 w-6 text-[#D4AF37]" />
            {eventTitle(event) || 'Untitled event'}
          </h2>
          <div className="flex flex-wrap items-center gap-2">
            <StatusPill tone={past ? 'slate' : meta.pillTone}>{meta.label}</StatusPill>
            {/* Only where the status does not already say it. */}
            {past && !['completed', 'archived'].includes(event?.status) && (
              <StatusPill tone="slate">Past</StatusPill>
            )}
            <span className="font-mono text-xs text-[#062B1A]/60">{event?.event_id}</span>
            {event?.staff_reference_code && (
              <span
                data-testid="header-staff-reference"
                title="Staff reference code — internal only, never published"
                className="rounded bg-[#062B1A]/5 px-1.5 py-0.5 font-mono text-xs text-[#062B1A]/60"
              >
                {event.staff_reference_code}
              </span>
            )}
            {/* The configured public address, not the internal slug. */}
            {event?.public_url && (
              <a href={event.public_url} target="_blank" rel="noreferrer"
                data-testid="header-public-url"
                className="inline-flex items-center gap-1 text-xs text-[#8A6508] hover:underline">
                {event.public_url} <ExternalLink className="size-3" />
              </a>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={locale} onValueChange={setLocale}>
            <SelectTrigger aria-label="Editing locale" className={cn('h-9 w-[9rem]', inputClass)}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(event?.locales || ['en']).map((code) => (
                <SelectItem key={code} value={code}>{localeLabel(code)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            type="button" variant="outline" onClick={duplicate} disabled={duplicating}
            data-testid="duplicate-event"
            className="border-[rgba(6,43,26,0.16)] text-[#062B1A]"
          >
            {duplicating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Copy className="mr-2 h-4 w-4" />}
            {duplicating ? 'Copying…' : 'Duplicate'}
          </Button>
          <Button onClick={() => save()} disabled={saving} data-testid="save-event"
            className="bg-[#062B1A] text-white hover:bg-[#041B10]">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-[rgba(6,43,26,0.10)]">
        {EVENT_EDITOR_TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            data-testid={`tab-${item.id}`}
            aria-current={tab === item.id ? 'page' : undefined}
            onClick={() => setTab(item.id)}
            className={cn(
              'rounded-t-lg px-3 py-2 text-sm font-medium transition-colors',
              tab === item.id
                ? 'border-b-2 border-[#D4AF37] text-[#062B1A]'
                : 'text-[#062B1A]/55 hover:text-[#062B1A]',
            )}
          >
            {item.label}
          </button>
        ))}
      </div>

      <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
        <CardContent className="space-y-5 p-5">
          {tab === 'basics' && (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <Field label="Title" htmlFor="title">
                <Input id="title" value={translation.title}
                  onChange={(e) => setTranslationField('title', e.target.value)} className={inputClass} />
              </Field>
              {/* Staff-only. Not a URL, not the event id, never published —
                  the public address is under Social & SEO. */}
              <Field
                label="Staff reference code"
                htmlFor="staff_reference_code"
                hint={canEditReference
                  ? 'Internal only. Lowercase letters, numbers and single hyphens, e.g. fsl-trial-2026-09-01.'
                  : referenceHint}
              >
                <Input
                  id="staff_reference_code"
                  value={form.staff_reference_code || ''}
                  disabled={!canEditReference}
                  onChange={(e) => setField('staff_reference_code', e.target.value)}
                  className={cn('font-mono', inputClass)}
                />
                {referenceErrors.map((message) => (
                  <p key={message} className="text-xs text-[rgb(153,27,27)]">{message}</p>
                ))}
              </Field>
              <Field label="Eyebrow" htmlFor="eyebrow" hint="Small line above the title.">
                <Input id="eyebrow" value={translation.eyebrow}
                  onChange={(e) => setTranslationField('eyebrow', e.target.value)} className={inputClass} />
              </Field>
              <Field label="Attendance mode" htmlFor="attendance_mode">
                <Select value={form.attendance_mode || 'online'}
                  onValueChange={(value) => setField('attendance_mode', value)}>
                  <SelectTrigger id="attendance_mode" aria-label="Attendance mode" className={inputClass}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ATTENDANCE_MODES.map((mode) => (
                      <SelectItem key={mode.value} value={mode.value}>{mode.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <div className="lg:col-span-2">
                <Field label="Summary" htmlFor="summary" hint="One or two sentences, used on cards and in search results.">
                  <Textarea id="summary" rows={3} value={translation.summary}
                    onChange={(e) => setTranslationField('summary', e.target.value)} className={inputClass} />
                </Field>
              </div>
              <Field label="Published locales" htmlFor="locales" hint="English is always published.">
                <div className="flex flex-wrap gap-2">
                  {EVENT_LOCALES.map((item) => {
                    const active = (form.locales || ['en']).includes(item.value);
                    return (
                      <Button
                        key={item.value} type="button" size="sm" variant="outline"
                        aria-pressed={active}
                        disabled={item.value === 'en'}
                        onClick={() => setField('locales', active
                          ? (form.locales || []).filter((code) => code !== item.value)
                          : [...(form.locales || ['en']), item.value])}
                        className={cn('h-8 rounded-full text-xs',
                          active ? 'border-[#D4AF37] bg-[#062B1A] text-white' : 'border-[#062B1A]/15 text-[#062B1A]')}
                      >
                        {item.label}
                      </Button>
                    );
                  })}
                </div>
              </Field>
              <div className="lg:col-span-2">
                <Field
                  label="Programs"
                  hint="Which program pages on the website show this event. Leave empty for an event that belongs to no one program."
                >
                  <ProgramPicker
                    options={programOptions}
                    selected={form.programs || []}
                    onChange={(value) => setField('programs', value)}
                  />
                </Field>
              </div>
              <Field label="Homepage feature" htmlFor="featured">
                <div className="flex items-center gap-3">
                  <Switch id="featured" checked={!!form.featured}
                    onCheckedChange={(value) => setField('featured', !!value)}
                    aria-label="Feature on the homepage" />
                  <Input type="number" min={0} aria-label="Homepage priority"
                    value={form.featured_priority ?? 0}
                    onChange={(e) => setField('featured_priority', Number(e.target.value) || 0)}
                    className={cn('h-9 w-24', inputClass)} />
                  <span className="text-xs text-[#062B1A]/45">Lower number shows first</span>
                </div>
              </Field>
            </div>
          )}

          {tab === 'schedule' && (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <Field label="Starts" htmlFor="start_at">
                <Input id="start_at" type="datetime-local" value={toLocalInput(form.start_at)}
                  onChange={(e) => setField('start_at', fromLocalInput(e.target.value))} className={inputClass} />
              </Field>
              <Field label="Ends" htmlFor="end_at">
                <Input id="end_at" type="datetime-local" value={toLocalInput(form.end_at)}
                  onChange={(e) => setField('end_at', fromLocalInput(e.target.value))} className={inputClass} />
              </Field>
              <Field label="Timezone" htmlFor="timezone">
                <Input id="timezone" value={form.timezone || ''}
                  onChange={(e) => setField('timezone', e.target.value)} className={inputClass} />
              </Field>
              <Field label="Capacity" htmlFor="capacity" hint="0 means no limit.">
                <Input id="capacity" type="number" min={0} value={form.capacity ?? 0}
                  onChange={(e) => setField('capacity', Number(e.target.value) || 0)} className={inputClass} />
              </Field>
              <Field label="Registration opens" htmlFor="registration_opens_at">
                <Input id="registration_opens_at" type="datetime-local"
                  value={toLocalInput(form.registration_opens_at)}
                  onChange={(e) => setField('registration_opens_at', fromLocalInput(e.target.value))}
                  className={inputClass} />
              </Field>
              <Field label="Registration closes" htmlFor="registration_closes_at">
                <Input id="registration_closes_at" type="datetime-local"
                  value={toLocalInput(form.registration_closes_at)}
                  onChange={(e) => setField('registration_closes_at', fromLocalInput(e.target.value))}
                  className={inputClass} />
              </Field>
              <div className="flex items-center gap-3 lg:col-span-2">
                <Switch id="waitlist_enabled" checked={!!form.waitlist_enabled}
                  onCheckedChange={(value) => setField('waitlist_enabled', !!value)}
                  aria-label="Offer a waitlist when full" />
                <Label htmlFor="waitlist_enabled">Offer a waitlist once capacity is reached</Label>
              </div>
            </div>
          )}

          {tab === 'content' && (
            <div className="space-y-6">
              <Field label="Detailed description" htmlFor="description_html"
                hint="Basic formatting only — the server strips anything else.">
                <Textarea id="description_html" rows={8} value={translation.description_html}
                  onChange={(e) => setTranslationField('description_html', e.target.value)}
                  className={cn('font-mono text-xs', inputClass)} />
              </Field>
              <ListEditor label="Benefits / highlights" items={translation.benefits}
                onChange={(value) => setTranslationField('benefits', value)} addLabel="Add benefit" />
              <ListEditor label="How it works" items={translation.how_it_works}
                onChange={(value) => setTranslationField('how_it_works', value)} addLabel="Add step" />
              <Field
                label="Agenda heading" htmlFor="agenda_label"
                hint={'Shown above the agenda panel. Left blank, the website uses "What to expect".'}
              >
                <Input id="agenda_label" value={translation.agenda_label || ''}
                  placeholder="What to expect"
                  onChange={(e) => setTranslationField('agenda_label', e.target.value)}
                  className={inputClass} />
              </Field>
              <ListEditor label="Agenda" items={translation.agenda} withTime
                onChange={(value) => setTranslationField('agenda', value)} addLabel="Add agenda row" />
              <ListEditor label="FAQs" items={translation.faqs}
                onChange={(value) => setTranslationField('faqs', value)} addLabel="Add question" />
            </div>
          )}

          {tab === 'registration' && (
            <div className="space-y-5">
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <Field label="Public location summary" htmlFor="location_summary"
                  hint="Shown on the website. Keep the exact address private below.">
                  <Input id="location_summary" value={translation.location_summary}
                    onChange={(e) => setTranslationField('location_summary', e.target.value)}
                    className={inputClass} />
                </Field>
                <Field label="Contact email" htmlFor="contact_email">
                  <Input id="contact_email" type="email" value={form.contact_email || ''}
                    onChange={(e) => setField('contact_email', e.target.value)} className={inputClass} />
                </Field>
              </div>

              <div className="rounded-lg border border-[rgba(212,175,55,0.45)] bg-[rgba(212,175,55,0.06)] p-4">
                <p className="mb-3 flex items-center gap-2 text-xs font-semibold text-[#8A6508]">
                  <AlertTriangle className="size-3.5" />
                  Private — never published. Sent to confirmed registrants only.
                </p>
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  <Field label="Physical address" htmlFor="private_address">
                    <Textarea id="private_address" rows={2} value={form.private_address || ''}
                      onChange={(e) => setField('private_address', e.target.value)} className={inputClass} />
                  </Field>
                  <Field label="Access instructions" htmlFor="private_access_instructions">
                    <Textarea id="private_access_instructions" rows={2}
                      value={form.private_access_instructions || ''}
                      onChange={(e) => setField('private_access_instructions', e.target.value)}
                      className={inputClass} />
                  </Field>
                  <Field label="Meeting link" htmlFor="private_meeting_url">
                    <Input id="private_meeting_url" value={form.private_meeting_url || ''}
                      onChange={(e) => setField('private_meeting_url', e.target.value)} className={inputClass} />
                  </Field>
                  <Field label="Meeting passcode" htmlFor="private_meeting_passcode">
                    <Input id="private_meeting_passcode" value={form.private_meeting_passcode || ''}
                      onChange={(e) => setField('private_meeting_passcode', e.target.value)}
                      className={inputClass} />
                  </Field>
                </div>
              </div>

              {registrations && (
                <div className="rounded-lg border border-[rgba(6,43,26,0.10)] p-4">
                  <div className="mb-2 flex items-center justify-between">
                    <p className="text-sm font-semibold text-[#062B1A]">
                      {registrations.total} registration{registrations.total === 1 ? '' : 's'}
                    </p>
                    <Button type="button" size="sm" variant="outline"
                      onClick={() => navigate(`/admin/events/${id}/registrations`)}
                      className="h-8 border-[rgba(6,43,26,0.16)] text-xs text-[#062B1A]">
                      Open attendee list
                    </Button>
                  </div>
                  <p className="text-xs text-[#062B1A]/60">
                    {registrations.no_photo_total} no-photo · {registrations.marketing_opt_in_total} marketing opt-in
                  </p>
                </div>
              )}
            </div>
          )}

          {tab === 'brochures' && (
            <div className="space-y-5">
              <div className="flex flex-wrap items-center gap-2">
                <input ref={fileInputRef} type="file" aria-label="Brochure file"
                  accept="application/pdf,image/jpeg,image/png,image/webp"
                  onChange={(e) => onUpload(e.target.files?.[0])} className="hidden" />
                <Button type="button" variant="outline" disabled={uploading}
                  onClick={() => fileInputRef.current?.click()}
                  className="border-[rgba(6,43,26,0.16)] text-[#062B1A]">
                  {uploading ? <Loader2 className="mr-2 size-4 animate-spin" /> : <Upload className="mr-2 size-4" />}
                  Upload existing brochure ({localeLabel(locale)})
                </Button>
                {templates.map((template) => (
                  <Button key={template.id} type="button" variant="outline" disabled={generating}
                    onClick={() => generate(template.id)}
                    className="border-[rgba(6,43,26,0.16)] text-[#062B1A]">
                    {generating ? <Loader2 className="mr-2 size-4 animate-spin" /> : <Plus className="mr-2 size-4" />}
                    Build {template.label}
                  </Button>
                ))}
              </div>

              <EventBrochureManager
                eventId={id}
                brochures={brochures}
                templates={templates}
                onChange={setBrochures}
                onRefresh={refreshBrochures}
                canManage={canManageBrochures}
              />
            </div>
          )}

          {tab === 'social' && (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {/* ─── Public routing ─── */}
              {/* Stored on the event, not compiled into either codebase. The
                  portal owns the configuration; the marketing site owns the
                  routes, so a saved path is never claimed to be live. */}
              <div className="lg:col-span-2" data-testid="public-routing">
                <div className="space-y-4 rounded-lg border border-[rgba(6,43,26,0.12)] p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-[#062B1A]/45">
                      Public routing
                    </p>
                    <div className="flex items-center gap-2">
                      <StatusPill tone={routeMeta.pillTone}>{routeMeta.label}</StatusPill>
                      <Button
                        type="button" size="sm" variant="outline"
                        disabled={!canEditRoute || checkingRoute || !event?.public_url}
                        onClick={runRouteCheck}
                        title={canEditRoute ? undefined : routeHint}
                        className="h-8 border-[rgba(6,43,26,0.16)] text-xs text-[#062B1A]"
                      >
                        {checkingRoute
                          ? <Loader2 className="mr-1 size-3.5 animate-spin" />
                          : <RefreshCw className="mr-1 size-3.5" />}
                        Check the website
                      </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                    <Field
                      label="Public event URL"
                      htmlFor="public_url"
                      hint={canEditRoute
                        ? 'HTTPS on an approved MCC domain. This is the address on posters and links.'
                        : routeHint}
                    >
                      <Input
                        id="public_url"
                        value={previewUrl}
                        disabled={!canEditRoute}
                        onChange={(e) => setPublicUrl(e.target.value)}
                        className={inputClass}
                      />
                    </Field>
                    <Field label="Public path" htmlFor="public_path"
                      hint="What the marketing site has to serve.">
                      <Input
                        id="public_path"
                        value={route.path || ''}
                        disabled={!canEditRoute}
                        placeholder={`/events/${form.slug || ''}`}
                        onChange={(e) => setRoute({ path: e.target.value })}
                        className={cn('font-mono', inputClass)}
                      />
                    </Field>
                    <Field label="Route destination" htmlFor="destination_type">
                      <Select
                        value={route.destination_type || 'event_page'}
                        disabled={!canEditRoute}
                        onValueChange={(value) => setRoute({ destination_type: value })}
                      >
                        <SelectTrigger id="destination_type" aria-label="Route destination" className={inputClass}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ROUTE_DESTINATION_TYPES.map((item) => (
                            <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>

                    {route.destination_type === 'program_page' && (
                      <Field label="Destination program" htmlFor="destination_program_id">
                        <Select
                          value={route.destination_program_id || ''}
                          disabled={!canEditRoute}
                          onValueChange={(value) => setRoute({ destination_program_id: value })}
                        >
                          <SelectTrigger id="destination_program_id" aria-label="Destination program" className={inputClass}>
                            <SelectValue placeholder="Choose a program" />
                          </SelectTrigger>
                          <SelectContent>
                            {programs.map((program) => (
                              <SelectItem key={program.id} value={program.id}>{program.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </Field>
                    )}
                    {route.destination_type === 'site_page' && (
                      <Field label="Destination page" htmlFor="destination_path"
                        hint="An existing MCC page, e.g. /programs-french-language.">
                        <Input id="destination_path" value={route.destination_path || ''}
                          disabled={!canEditRoute}
                          onChange={(e) => setRoute({ destination_path: e.target.value })}
                          className={cn('font-mono', inputClass)} />
                      </Field>
                    )}
                    {route.destination_type === 'external_url' && (
                      <Field label="Destination URL" htmlFor="destination_url"
                        hint="HTTPS on an approved MCC domain.">
                        <Input id="destination_url" value={route.destination_url || ''}
                          disabled={!canEditRoute}
                          onChange={(e) => setRoute({ destination_url: e.target.value })}
                          className={inputClass} />
                      </Field>
                    )}

                    <Field label="Related program" htmlFor="related_program_id"
                      hint="Used for “Explore the program”, recommendations and follow-up links.">
                      <Select
                        value={route.related_program_id || ''}
                        disabled={!canEditRoute}
                        onValueChange={(value) => setRoute({ related_program_id: value })}
                      >
                        <SelectTrigger id="related_program_id" aria-label="Related program" className={inputClass}>
                          <SelectValue placeholder="None" />
                        </SelectTrigger>
                        <SelectContent>
                          {programs.map((program) => (
                            <SelectItem key={program.id} value={program.id}>{program.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field label="Route state" htmlFor="route_status"
                      hint="Verified is set by checking the website, never by saving.">
                      <Select
                        value={ROUTE_STATUS_OPTIONS.includes(route.status) ? route.status : 'not_checked'}
                        disabled={!canEditRoute}
                        onValueChange={(value) => setRoute({ status: value })}
                      >
                        <SelectTrigger id="route_status" aria-label="Route state" className={inputClass}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ROUTE_STATUS_OPTIONS.map((status) => (
                            <SelectItem key={status} value={status}>
                              {routeStatusMeta(status).label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                  </div>

                  <div className="rounded-lg bg-[#F7F8FA] p-3 text-xs text-[#062B1A]/70">
                    <p>
                      Visitors will go to{' '}
                      <span data-testid="route-preview" className="font-mono text-[#062B1A]">
                        {previewUrl || '—'}
                      </span>
                    </p>
                    {event?.public_route?.status !== 'verified' && (
                      <p className="mt-1 text-[#8A6508]">
                        Saving this here does not create the route on the marketing website. If the
                        site cannot serve this path yet, it stays “Needs website setup” until a
                        check says otherwise.
                      </p>
                    )}
                    {event?.public_route?.status_note && (
                      <p className="mt-1">{event.public_route.status_note}</p>
                    )}
                  </div>
                </div>
              </div>

              <Field label="SEO title" htmlFor="seo_title" hint="Up to 70 characters.">
                <Input id="seo_title" value={translation.seo_title}
                  onChange={(e) => setTranslationField('seo_title', e.target.value)} className={inputClass} />
              </Field>
              <Field label="Meta description" htmlFor="meta_description" hint="Up to 200 characters.">
                <Input id="meta_description" value={translation.meta_description}
                  onChange={(e) => setTranslationField('meta_description', e.target.value)} className={inputClass} />
              </Field>
              <Field label="Social title" htmlFor="social_title">
                <Input id="social_title" value={translation.social_title}
                  onChange={(e) => setTranslationField('social_title', e.target.value)} className={inputClass} />
              </Field>
              <Field label="Social summary" htmlFor="social_summary">
                <Textarea id="social_summary" rows={2} value={translation.social_summary}
                  onChange={(e) => setTranslationField('social_summary', e.target.value)} className={inputClass} />
              </Field>
              <div className="lg:col-span-2">
                <ListEditor
                  label="Campaign links"
                  items={(form.campaign_links || []).map((link) => ({ title: link.label, body: link.url }))}
                  onChange={(rows) => setField('campaign_links',
                    rows.map((row) => ({ label: row.title, url: row.body })))}
                  addLabel="Add campaign link"
                />
                <p className="mt-2 text-xs text-[#062B1A]/45">
                  Heading is the channel name; the body holds the UTM-tagged URL.
                </p>
              </div>
              {event?.public_url && (
                <div className="flex flex-wrap gap-2 lg:col-span-2">
                  <Button type="button" variant="outline"
                    onClick={() => {
                      // The configured public address — the same one the QR
                      // codes, brochures and share links use.
                      navigator.clipboard?.writeText(event.public_url);
                      toast.success('Public link copied');
                    }}
                    className="border-[rgba(6,43,26,0.16)] text-[#062B1A]">
                    <Copy className="mr-2 size-4" /> Copy public link
                  </Button>
                  <Button type="button" variant="outline" asChild
                    className="border-[rgba(6,43,26,0.16)] text-[#062B1A]">
                    <a href={event.public_url} target="_blank" rel="noreferrer">
                      <ExternalLink className="mr-2 size-4" /> Open public page
                    </a>
                  </Button>
                </div>
              )}
            </div>
          )}

          {tab === 'agencies' && (
            <EventAgencyLinks eventId={id} canManage={canManageLinks} />
          )}

          {tab === 'legal' && (
            <div className="space-y-4">
              <p className="text-sm text-[#062B1A]/70">
                Every registration stores the exact policy versions the registrant was shown, plus
                their answers. Consent is asked, never assumed: nothing is preselected, and choosing
                “no” to photography or marketing never blocks a registration.
              </p>
              <div className="rounded-lg border border-[rgba(6,43,26,0.10)] bg-[#F7F8FA] p-4 text-sm text-[#062B1A]/75">
                <p className="mb-2 font-semibold text-[#062B1A]">Required of every registrant</p>
                <ul className="list-disc space-y-1 pl-5">
                  <li>Agreement to the Privacy Policy.</li>
                  <li>Agreement to the Terms and Conditions and Event Terms.</li>
                  <li>Acknowledgement that photography and video may take place.</li>
                  <li>An explicit yes or no on photography use.</li>
                  <li>An explicit yes or no on future marketing.</li>
                </ul>
                <p className="mt-3">
                  Under-19 registrants additionally need a named parent or legal guardian.
                </p>
              </div>
              <Button type="button" variant="outline" onClick={() => navigate('/admin/legal-policies')}
                className="border-[rgba(6,43,26,0.16)] text-[#062B1A]">
                Manage policy versions
              </Button>
            </div>
          )}

          {tab === 'publish' && (
            <div className="space-y-5">
              {blockers.length > 0 && (
                <div data-testid="publish-blockers"
                  className="rounded-lg border border-[rgba(153,27,27,0.25)] bg-[rgba(254,242,242,0.9)] p-4">
                  <p className="mb-2 text-sm font-semibold text-[rgb(153,27,27)]">
                    Not ready to publish
                  </p>
                  <ul className="list-disc space-y-1 pl-5 text-sm text-[rgb(153,27,27)]">
                    {blockers.map((blocker) => <li key={blocker}>{blocker}</li>)}
                  </ul>
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="outline" onClick={loadPreview}
                  className="border-[rgba(6,43,26,0.16)] text-[#062B1A]">
                  <Eye className="mr-2 size-4" /> Preview as the website sees it
                </Button>
                {['review', 'published', 'closed', 'completed', 'archived', 'draft'].map((status) => (
                  canTransition(event, status) && (
                    <Button
                      key={status} type="button"
                      variant={status === 'published' ? 'default' : 'outline'}
                      disabled={status === 'published' && !isPublishable(event)}
                      onClick={() => moveStatus(status)}
                      className={status === 'published'
                        ? 'bg-[#062B1A] text-white hover:bg-[#041B10]'
                        : 'border-[rgba(6,43,26,0.16)] text-[#062B1A]'}
                    >
                      {status === 'published' ? 'Publish' : `Move to ${eventStatusMeta(status).label.toLowerCase()}`}
                    </Button>
                  )
                ))}
                <Button
                  type="button" variant="outline" onClick={() => setConfirmDelete(true)}
                  data-testid="delete-event"
                  className="border-[rgba(153,27,27,0.3)] text-[rgb(153,27,27)]"
                >
                  <Trash2 className="mr-2 size-4" /> Delete event
                </Button>
              </div>

              {preview && (
                <div data-testid="event-preview"
                  className="space-y-2 rounded-lg border border-[rgba(6,43,26,0.10)] p-4">
                  <p className="text-sm font-semibold text-[#062B1A]">{preview.title}</p>
                  <p className="text-sm text-[#062B1A]/70">{preview.summary}</p>
                  <p className="text-xs text-[#062B1A]/50">
                    {preview.location_summary} · {attendanceModeLabel(preview.attendance_mode)} ·{' '}
                    {preview.registration?.reason}
                  </p>
                  <p className="text-xs text-[#062B1A]/45">
                    {preview.policies?.length || 0} policy version(s) will be recorded with each registration.
                  </p>
                </div>
              )}

              {/* Folded away by default: the internal ids and the alias history
                  are for tracing a record, not for reading it — and the slug in
                  particular must not be mistaken for the live public address. */}
              <div className="rounded-lg border border-[rgba(6,43,26,0.10)] p-4">
                <button
                  type="button"
                  onClick={() => setTechnicalOpen((current) => !current)}
                  aria-expanded={technicalOpen}
                  className="flex w-full items-center justify-between text-left text-[11px] font-semibold uppercase tracking-wide text-[#062B1A]/45 hover:text-[#062B1A]/70"
                >
                  Audit &amp; technical details
                  <ChevronDown className={cn('size-3.5 transition-transform', technicalOpen && 'rotate-180')} />
                </button>

                {technicalOpen && (
                  <div className="mt-4 space-y-5" data-testid="technical-details">
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                      <Field
                        label="Internal website slug"
                        htmlFor="slug"
                        hint="Technical identifier for the standard event page. Not the public address."
                      >
                        <Input id="slug" value={form.slug || ''}
                          onChange={(e) => setField('slug', e.target.value)}
                          className={cn('font-mono', inputClass)} />
                      </Field>
                      <div className="space-y-1.5">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-[#062B1A]/45">
                          Immutable identifiers
                        </p>
                        <p className="font-mono text-xs text-[#062B1A]/70">{event?.id}</p>
                        <p className="font-mono text-xs text-[#062B1A]/70">{event?.event_id}</p>
                        <p className="text-xs text-[#062B1A]/45">
                          The UUID every registration joins on, and the system id staff quote.
                          Neither ever changes.
                        </p>
                      </div>
                    </div>

                    <div>
                      <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#062B1A]/45">
                        Previous staff reference codes
                      </p>
                      {referenceAliases.length === 0 ? (
                        <p className="text-sm text-[#062B1A]/45">None — this code has not changed.</p>
                      ) : (
                        <div className="space-y-1">
                          {referenceAliases.map((alias) => (
                            <div key={alias.code} data-testid="reference-alias"
                              className="flex flex-wrap items-center gap-2 text-xs text-[#062B1A]/70">
                              <span className="font-mono">{alias.code}</span>
                              <span>{alias.released ? 'released' : 'still resolves here'}</span>
                              {!alias.released && (
                                <Button type="button" size="sm" variant="ghost"
                                  disabled={!canEditReference}
                                  title={canEditReference ? undefined : referenceHint}
                                  onClick={() => releaseAlias('staff_reference', alias.code)}
                                  className="h-6 px-2 text-[11px] text-[rgb(153,27,27)]">
                                  Release
                                </Button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div>
                      <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#062B1A]/45">
                        Previous public paths
                      </p>
                      {routeAliases.length === 0 ? (
                        <p className="text-sm text-[#062B1A]/45">None — this address has not changed.</p>
                      ) : (
                        <div className="space-y-1">
                          {routeAliases.map((alias) => (
                            <div key={alias.path_key} data-testid="route-alias"
                              className="flex flex-wrap items-center gap-2 text-xs text-[#062B1A]/70">
                              <span className="font-mono">{alias.path}</span>
                              <span>
                                {alias.released
                                  ? 'released'
                                  : 'still redirects here — old posters and QR codes keep working'}
                              </span>
                              {!alias.released && (
                                <Button type="button" size="sm" variant="ghost"
                                  disabled={!canEditRoute}
                                  title={canEditRoute ? undefined : routeHint}
                                  onClick={() => releaseAlias('public_path', alias.path)}
                                  className="h-6 px-2 text-[11px] text-[rgb(153,27,27)]">
                                  Release
                                </Button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div>
                      <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#062B1A]/45">
                        Audit history
                      </p>
                      {audit.length === 0 ? (
                        <p className="text-sm text-[#062B1A]/45">No changes recorded yet.</p>
                      ) : (
                        <div className="space-y-1">
                          {audit.map((entry) => (
                            <div key={entry.id} data-testid="audit-entry"
                              className="flex flex-wrap items-baseline gap-2 text-xs text-[#062B1A]/70">
                              <span className="font-mono text-[11px] text-[#062B1A]/45">
                                {formatDateTime(entry.created_at)}
                              </span>
                              <span className="font-semibold text-[#062B1A]">{entry.action}</span>
                              <span>by {entry.actor_name}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Nothing is undeletable, but a published page or somebody's
          registration is never lost by accident — what goes with the event is
          on screen before the button is. */}
      <Dialog open={confirmDelete} onOpenChange={(open) => { if (!open) setConfirmDelete(false); }}>
        <DialogContent className="sm:max-w-lg" data-testid="confirm-delete-event">
          <DialogHeader>
            <DialogTitle>Delete this event?</DialogTitle>
            <DialogDescription>
              “{eventTitle(event) || 'Untitled event'}” and its brochures and agency links
              will be removed. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {deleteImpact.warnings.length > 0 && (
            <ul
              data-testid="delete-event-warnings"
              className="list-disc space-y-1 rounded-lg border border-[rgba(153,27,27,0.25)] bg-[rgba(254,242,242,0.9)] py-3 pl-8 pr-4 text-sm text-[rgb(153,27,27)]"
            >
              {deleteImpact.warnings.map((warning) => <li key={warning}>{warning}</li>)}
            </ul>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setConfirmDelete(false)}
              className="border-[rgba(6,43,26,0.16)] text-[#062B1A]">
              Keep it
            </Button>
            <Button type="button" disabled={deleting} onClick={removeEvent}
              className="bg-[rgb(153,27,27)] text-white hover:bg-[rgb(127,22,22)]">
              {deleting ? <Loader2 className="mr-2 size-4 animate-spin" /> : <Trash2 className="mr-2 size-4" />}
              {deleting ? 'Deleting…' : 'Delete event'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Changing an identifier an event already has is a decision, so the
          server refuses until it is confirmed and both values are on screen. */}
      <Dialog open={!!confirmChange} onOpenChange={(open) => { if (!open) setConfirmChange(null); }}>
        <DialogContent className="sm:max-w-lg" data-testid="confirm-identity-change">
          <DialogHeader>
            <DialogTitle>
              {confirmChange?.field === 'staff_reference_code'
                ? 'Change the staff reference code?'
                : 'Change the public event URL?'}
            </DialogTitle>
            <DialogDescription>
              {confirmChange?.field === 'staff_reference_code'
                ? 'Registrations, the event UUID, the EVT id, the internal slug, the public URL '
                  + 'and consent evidence are all untouched. The old code keeps resolving to this '
                  + 'event until a full administrator releases it.'
                : 'The old address keeps redirecting here, so printed brochures and QR codes carry '
                  + 'on working. The route goes back to “Needs website setup” until a check of the '
                  + 'marketing site confirms the new one.'}
            </DialogDescription>
          </DialogHeader>
          <dl className="space-y-2 rounded-lg border border-[rgba(6,43,26,0.10)] bg-[#F7F8FA] p-3 text-sm">
            <div className="flex justify-between gap-3">
              <dt className="text-[#062B1A]/60">Current</dt>
              <dd className="font-mono text-xs text-[#062B1A]">{confirmChange?.current || '—'}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-[#062B1A]/60">Proposed</dt>
              <dd className="font-mono text-xs text-[#062B1A]">{confirmChange?.proposed || '—'}</dd>
            </div>
          </dl>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setConfirmChange(null)}
              className="border-[rgba(6,43,26,0.16)] text-[#062B1A]">
              Keep the current one
            </Button>
            <Button type="button" disabled={saving} onClick={confirmAndSave}
              className="bg-[#062B1A] text-white hover:bg-[#041B10]">
              {saving && <Loader2 className="mr-2 size-4 animate-spin" />}
              Save the change
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
